import { useEffect, useState } from 'react';
import { promoCodeAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Ticket, Percent, Euro, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPromoCodes() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: 10,
    min_purchase: 0,
    max_uses: 0,
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  const loadData = async () => {
    try {
      const response = await promoCodeAPI.getAll();
      setPromoCodes(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setForm({
      code: '',
      discount_type: 'percent',
      discount_value: 10,
      min_purchase: 0,
      max_uses: 0,
      valid_from: '',
      valid_until: '',
      is_active: true
    });
    setEditing(null);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, code });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.code) {
      toast.error('Entrez un code');
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        ...form,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null
      };
      
      if (editing) {
        await promoCodeAPI.update(editing.id, data);
        toast.success('Code promo mis à jour');
      } else {
        await promoCodeAPI.create(data);
        toast.success('Code promo créé');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    
    try {
      await promoCodeAPI.delete(id);
      toast.success('Code promo supprimé');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      min_purchase: promo.min_purchase,
      max_uses: promo.max_uses,
      valid_from: promo.valid_from?.split('T')[0] || '',
      valid_until: promo.valid_until?.split('T')[0] || '',
      is_active: promo.is_active
    });
    setDialogOpen(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié !');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
            Codes Promo
          </h1>
          <p className="text-gray-500">Créez et gérez vos codes de réduction</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-promo-code-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Nouveau'} Code Promo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="promo-code">Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="promo-code"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: SUMMER20"
                    required
                    className="font-mono"
                    data-testid="promo-code-input"
                  />
                  <Button type="button" variant="outline" onClick={generateCode}>
                    Générer
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discount-type">Type de réduction</Label>
                  <Select
                    value={form.discount_type}
                    onValueChange={(value) => setForm({ ...form, discount_type: value })}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="discount-value">Valeur</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="discount-value"
                      type="number"
                      min="1"
                      step={form.discount_type === 'fixed' ? '0.01' : '1'}
                      value={form.discount_value}
                      onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) })}
                      required
                      data-testid="discount-value-input"
                    />
                    <span className="text-gray-500">{form.discount_type === 'percent' ? '%' : '€'}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min-purchase">Minimum d'achat (€)</Label>
                  <Input
                    id="min-purchase"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_purchase}
                    onChange={(e) => setForm({ ...form, min_purchase: parseFloat(e.target.value) || 0 })}
                    placeholder="0 = pas de minimum"
                    data-testid="min-purchase-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max-uses">Utilisations max</Label>
                  <Input
                    id="max-uses"
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 0 })}
                    placeholder="0 = illimité"
                    data-testid="max-uses-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid-from">Valide à partir du</Label>
                  <Input
                    id="valid-from"
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                    data-testid="valid-from-input"
                  />
                </div>
                <div>
                  <Label htmlFor="valid-until">Valide jusqu'au</Label>
                  <Input
                    id="valid-until"
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    data-testid="valid-until-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="promo-active">Actif</Label>
                <Switch
                  id="promo-active"
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  data-testid="promo-active-switch"
                />
              </div>

              <Button type="submit" className="w-full" disabled={saving} data-testid="save-promo-code-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Promo Codes List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promoCodes.map((promo) => (
          <Card key={promo.id} className={`border-0 shadow-sm ${!promo.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <button
                      onClick={() => copyCode(promo.code)}
                      className="font-mono font-bold text-lg hover:text-[#7C3AED] flex items-center gap-1"
                      title="Copier le code"
                    >
                      {promo.code}
                      <Copy className="w-3 h-3" />
                    </button>
                    <Badge className={promo.discount_type === 'percent' ? 'bg-blue-500' : 'bg-blue-500'}>
                      {promo.discount_type === 'percent' ? (
                        <><Percent className="w-3 h-3 mr-1" />{promo.discount_value}%</>
                      ) : (
                        <><Euro className="w-3 h-3 mr-1" />{promo.discount_value}€</>
                      )}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(promo)}
                    data-testid={`edit-promo-${promo.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(promo.id)}
                    data-testid={`delete-promo-${promo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                {promo.min_purchase > 0 && (
                  <div className="flex items-center gap-2">
                    <span>Min. achat:</span>
                    <span className="font-semibold">{promo.min_purchase}€</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span>Utilisations:</span>
                  <span className="font-semibold">
                    {promo.current_uses}{promo.max_uses > 0 ? `/${promo.max_uses}` : ' (illimité)'}
                  </span>
                </div>
                {(promo.valid_from || promo.valid_until) && (
                  <div className="text-xs text-gray-400">
                    {promo.valid_from && `Du ${new Date(promo.valid_from).toLocaleDateString('fr-FR')}`}
                    {promo.valid_from && promo.valid_until && ' '}
                    {promo.valid_until && `au ${new Date(promo.valid_until).toLocaleDateString('fr-FR')}`}
                  </div>
                )}
              </div>

              {!promo.is_active && (
                <Badge variant="outline" className="mt-2 text-amber-600 border-amber-600">
                  Inactif
                </Badge>
              )}

              {promo.max_uses > 0 && promo.current_uses >= promo.max_uses && (
                <Badge variant="outline" className="mt-2 text-red-600 border-red-600">
                  Épuisé
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {promoCodes.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          Aucun code promo créé
        </div>
      )}
    </div>
  );
}
